import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import { useAuthStore } from '../../store/authStore';

function CartIcon({ count }: { count: number }) {
  return (
    <Link to="/cart" className="relative group flex items-center justify-center w-11 h-11 rounded-full hover:bg-champagne transition-colors">
      <svg className="w-7 h-7 text-text-dark group-hover:text-chocolate transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.4 7h12.8M7 13L5.4 5M17 21a1 1 0 11-2 0 1 1 0 012 0zm-8 0a1 1 0 11-2 0 1 1 0 012 0z" />
      </svg>
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-chocolate text-white text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </Link>
  );
}

function UserMenu({ customerName, isAdmin, onLogout }: { customerName: string | null; isAdmin: boolean; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const firstName = customerName?.split(' ')[0] ?? '';
  const initial = firstName.charAt(0).toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full border transition-colors ${
          open ? 'bg-champagne border-champagne-dark' : 'border-champagne hover:border-champagne-dark hover:bg-champagne-light'
        }`}
      >
        <div className="w-8 h-8 rounded-full bg-chocolate/10 flex items-center justify-center">
          <span className="text-sm font-semibold text-chocolate">{initial}</span>
        </div>
        <span className="font-medium text-sm text-text-dark hidden sm:block">{firstName}</span>
        <svg className={`w-3 h-3 text-text-light transition-transform duration-200 hidden sm:block ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-48 bg-white rounded-xl shadow-lg border border-champagne py-1.5 z-50">
          <Link
            to="/perfil"
            onClick={() => setOpen(false)}
            className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
              pathname === '/perfil' ? 'text-chocolate bg-champagne-light' : 'text-text-dark hover:bg-champagne-light'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Meu Perfil
          </Link>

          <Link
            to="/perfil?tab=pedidos"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-text-dark hover:bg-champagne-light transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Meus Pedidos
          </Link>

          {isAdmin && (
            <Link
              to="/configuracoes"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                pathname === '/configuracoes' ? 'text-chocolate bg-champagne-light' : 'text-text-dark hover:bg-champagne-light'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Configurações
            </Link>
          )}

          <div className="border-t border-champagne my-1.5" />

          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors w-full"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const totalItems = useCartStore((s) => s.totalItems());
  const { isAuthenticated, customerName, isAdmin, logout } = useAuthStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-champagne sticky top-0 z-50">
      {/* Faixa superior discreta */}
      <div className="bg-text-dark text-champagne-light text-[15px] text-center py-1.5 tracking-wide hidden sm:block">
        Frete e entrega combinados via WhatsApp&nbsp;&nbsp;·&nbsp;&nbsp;
        <a href="https://wa.me/5585994210413" target="_blank" rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-chocolate-light transition-colors">
          (85) 99421-0413
        </a>
      </div>

      {/* Corpo do header */}
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group shrink-0">
          <img
            src="/icone.png"
            alt="Baby Chic"
            className="w-9 h-9 object-contain"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div className="flex flex-col leading-tight">
            <span className="text-lg font-bold text-chocolate tracking-wide group-hover:text-chocolate-dark transition-colors">
              Baby Chic
            </span>
            <span className="text-[10px] text-text-light tracking-[0.18em] uppercase hidden sm:block">
              Moda Infantil
            </span>
          </div>
        </Link>

        {/* Nav central */}
        <nav className="hidden md:flex items-center gap-4">
          <Link
            to="/"
            className={[
              'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200',
              pathname === '/'
                ? 'bg-chocolate text-white shadow-sm'
                : 'text-text-medium border border-champagne-dark hover:border-chocolate hover:text-chocolate hover:bg-champagne-light',
            ].join(' ')}
          >
            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Catálogo
          </Link>
        </nav>

        {/* Ações direita */}
        <div className="flex items-center gap-1">
          <CartIcon count={totalItems} />

          <div className="w-px h-7 bg-champagne-dark mx-2 hidden sm:block" />

          {isAuthenticated ? (
            <UserMenu customerName={customerName} isAdmin={isAdmin} onLogout={handleLogout} />
          ) : (
            <Link
              to="/login"
              className="text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark px-5 py-2 rounded-full transition-colors"
            >
              Entrar
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
